
import { AfterViewInit, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, ReactiveFormsModule, ValidatorFn, Validators } from '@angular/forms';
import { SelectUserProfileFieldComponent } from "../../../../components/options/select-user-profile-field/select-user-profile-field.component";
import { AlertNotificationService } from '../../../../services/alert-notification.service';
import { User } from '../../../../models/entities/users';
import { SavedDataInfo } from '../../../../models/form/saved-data-info';
import { AlertInfo } from '../../../../models/alert-info';
import { NgbAlert } from '@ng-bootstrap/ng-bootstrap';
import { FormService } from '../../../../services/form.service';
import { SelectCompanyFieldComponent } from '../../../../components/form/selects/select-company-field/select-company-field.component';
import { UsersProfileService } from '../../../../services/api/users-profile.service';
import { ActivatedRoute } from '@angular/router';
import { CompaniesService } from 'src/app/services/api/general-settings/companies.service';
import { ConfirmationDialogService } from '../../../../services/confirmation-dialog.service';
import { OptionService } from '../../../../services/api/option.service';
import { forkJoin } from 'rxjs';

@Component({
  selector: 'profile-company',
  imports: [ReactiveFormsModule, SelectUserProfileFieldComponent, SelectUserProfileFieldComponent, NgbAlert, SelectCompanyFieldComponent],
  templateUrl: './profile-company-form.component.html',
  styleUrl: './profile-company-form.component.scss'
})
export class FormProfileCompanyComponent implements OnInit, OnChanges {
  @Input() user?: User
  @Input() customer!: any;
  @Input() readonly: boolean = false;

  form: FormGroup;
  itemToBeDeleted!: FormArray;
  indexItemToBeDeleted: number = 0;
  viewButton: boolean | undefined = true
  companies: any[] = [];
  profileOptions: { key: string, value: string }[] = [];
  alertInfo: AlertInfo = { show: false, type: '', message: '' };
  initialProfileIds: number[] = [];
  private optionsLoaded: boolean = false;

  constructor(
    private fb: FormBuilder,
    private companiesService: CompaniesService,
    private optionService: OptionService,
    private alertNotificationService: AlertNotificationService,
    private formService: FormService,
    private userProfileService: UsersProfileService,
    private route: ActivatedRoute,
    private confirmationDialogService: ConfirmationDialogService
  ) {
    this.form = this.fb.group({
      profiles: this.fb.array([])
    });
  }

  ngOnInit() {
    this.loadOptions();
  }
  ngOnChanges(changes: SimpleChanges) {
    if (changes['user'] && changes['user'].currentValue) {
      if (this.user) {
        if (this.optionsLoaded) {
          this.loadUserProfiles();
        }
      }
    }
  }

  private loadOptions() {
    forkJoin({
      companies: this.companiesService.list(),
      profileOptions: this.optionService.userProfile()
    }).subscribe({
      next: ({ companies, profileOptions }: any) => {
        this.companies = companies?.data?.companies ?? [];
        this.profileOptions = profileOptions?.data?.options ?? [];
        this.optionsLoaded = true;

        if (this.user) {
          this.loadUserProfiles();
        }
      },
      error: (error: any) => {
        console.error('Error in loading Company/Profile options:', error);
        this.alertNotificationService.error('Unable to load Company/Profile options');
      }
    });
  }

  private loadUserProfiles() {
    if (this.user){
      this.userProfileService.list(this.user?.userId).subscribe({
      next: (response: any) => {

        const profilesArray = this.getProfiles();
        profilesArray.clear();
        this.initialProfileIds = response?.data?.userProfiles
          .map((userProfile: any) => userProfile.userProfileId)
          .filter((id: number) => id != null);        

        if (response?.data?.userProfiles && response.data.userProfiles.length > 0) {
          response.data.userProfiles.forEach((userProfile: any) => {
            const newProfileGroup = this.createProfileFormGroup();
            newProfileGroup.patchValue({
              userProfileId: userProfile.userProfileId,
              profile: userProfile.profile,
              companyId: userProfile.company?.companyId ?? null
            });

            if (newProfileGroup.get('companyId')?.value === null) {
              newProfileGroup.get('companyId')?.setValue(null); 
              newProfileGroup.get('companyId')?.markAsPristine(); 
          }
            Object.keys(newProfileGroup.controls).forEach(key => {
              const control = newProfileGroup.get(key);
              control?.markAsPristine();
              control?.markAsUntouched();
              control?.updateValueAndValidity();
            });

            profilesArray.push(newProfileGroup);
          });

          this.form.markAsPristine();
          this.form.markAsUntouched();
        }
        /*
        else {

          this.addNewProfile(profilesArray);
        }
          */
      },
      error: (error: any) => {
        console.error('Error loading the profiles:', error);
        this.alertNotificationService.error('Unable to load existing profiles');
      }
    });
    }
  }

  getProfiles(): FormArray {
    return this.form.get('profiles') as FormArray;
  }

  getFormControl(formGroup: FormGroup, controlName: string): FormControl {
    return formGroup.get(controlName) as FormControl;
  }

  getProfileSelect(index: number): FormControl {
    return this.getFormControl(this.getProfiles().at(index) as FormGroup, 'profile');
  }

  getCompanySelect(index: number): FormControl {
    return this.getFormControl(this.getProfiles().at(index) as FormGroup, 'companyId');
  }

  private createProfileFormGroup() {
    const profilesArray = this.getProfiles();
    const existingUserProfileIds = profilesArray.controls
      .map(profile => profile.get('userProfileId')?.value)
      .filter((id: number | null) => id != null);

    const newUserProfileId = existingUserProfileIds.length > 0
      ? Math.max(...existingUserProfileIds) + 1
      : 1;

    return this.fb.group({
      userProfileId: new FormControl(newUserProfileId, [Validators.required]),
      profile: new FormControl('', [Validators.required]),
      companyId: new FormControl('', [this.companyIdValidator(null)])
    });
  }

  addNewProfile(formArray: FormArray) {
    formArray.push(this.createProfileFormGroup());
  }

  deleteProfile(formArray: FormArray, index: number) {

    this.userProfileService.delete(this.user!.userId, formArray.at(index).get('userProfileId')?.value).subscribe({
      next: (response: any) => {
        formArray.removeAt(index);
        this.alertNotificationService.success('Operation completed successfully');
      },
      error: (error: any) => {
        console.error('Error deleting the profile:', error);
        this.alertNotificationService.error('Unable to delete the profile');
      }
    });
  }

  confirmDelete(formArray: FormArray, index: number, item: string) {
    this.itemToBeDeleted = formArray;
    this.indexItemToBeDeleted = index;
    this.confirmationDialogService.open(
      `Are you sure to delete this ${item}?`,
      () => this.deleteProfile(formArray, index)
    );
  }

  /**
   * 
   * *******************
   * Submit form methods
   * *******************
   *
   **/

  showAlertInfo(type: string, message: string) {
    this.alertInfo = {
      show: true,
      type,
      message
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setTimeout(() => {
      this.alertInfo.show = false;
    }, 3000);
  }
  updateValidationErrors(formGroup: FormGroup, validationErrors: any) {
    this.formService.resetErrors(formGroup);
    this.formService.setValidationErrors(formGroup, validationErrors);
  }

  submitFormSuccess(count: number) {
    this.alertNotificationService.success(`${count} profiles company have been saved!`)
    this.showAlertInfo('success', `${count} profiles company have been saved!`);
  }

  submitFormError(formGroup: FormGroup, error: any) {

    if (error.error.error.description == 'ARGUMENT_VALIDATION_ERROR') {
      this.updateValidationErrors(formGroup, error.error.error.validationErrors);
    }

  }

  async submitForm() {
    const userProfilesFG = this.getProfiles().controls as FormGroup[];

    userProfilesFG.forEach((profile: FormGroup) => {
      Object.keys(profile.controls).forEach(key => {
        const control = profile.get(key);
        control?.markAsTouched();
        control?.updateValueAndValidity();
      });
    });

    const requests = userProfilesFG.map(async (userProfileFG, index) => {
      if (userProfileFG.valid) {
        const values = userProfileFG.value;
        const userProfileId = Number(values.userProfileId);
        const companyId = Number(values.companyId);
        const userId = Number(this.user?.userId);
        const profile = values.profile;

        /*
        if (isNaN(companyId)) {
          this.alertNotificationService.error(`Invalid company ID for profile ${values.profile}`);
          return { success: false, index };
        }
          */
         
        const dataPOST = {
          profile,
          companyId,
          userId
        };
        const dataPUT ={
          profile,
          companyId,
        }

        try {
          const isNewProfile = !this.initialProfileIds.includes(values.userProfileId);
        
          const serviceMethod = isNewProfile
           ? this.userProfileService.create(this.user?.userId, dataPOST)
            : this.userProfileService.update(this.user!.userId, userProfileId, dataPUT);
        
          await serviceMethod.toPromise();
          //this.submitFormSuccess(data)
          console.log('dataToSend', dataPOST, serviceMethod);
          console.log(this.initialProfileIds);
          return { success: true, index };
        } catch (error) {
          this.submitFormError(userProfileFG, error);
          return { success: false, index };
        }
      }

      return { success: false, index };

    });

    const results = await Promise.all(requests);

    const successfulProfiles = results.filter(r => r.success);
    if (successfulProfiles.length == userProfilesFG.length) {
      this.submitFormSuccess(successfulProfiles.length);
    }
    else {
      this.showAlertInfo('danger', 'Some profiles companies are not saved');
    }
  }

  /**
 * 
 * *******************
 * Submit form methods
 * *******************
 *
 **/

 companyIdValidator(initialValue: any): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      const isValid = control.value === null || control.value !== initialValue;
      return isValid ? null : { invalidCompanyId: { value: control.value } };
    };
  }
}